"use client";

import Image from "next/image";
import { useCallback, useTransition, useState, useEffect } from "react";
import { Trans, useLingui } from "@lingui/react/macro";

import { Button } from "@/components/ui/button";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { useMountEffect } from "@/hooks/use-mount-effect";
import {
  applyPublicLocaleSwitch,
  navigateToPublicRoute,
  writeLocaleCookieClient,
} from "@/components/marketing/public-locale-switch";
import { SUPPORTED_LOCALES, localePickerLabels } from "@/lib/i18n/locales";
import { authClient } from "@/lib/auth-client";
import { persistAuthRedirect } from "@/lib/auth-redirect-client";

// Static href list — labels are translated inside the component using t`...`
const navLinkHrefs = [
  { href: "/", labelKey: "Home" },
  { href: "/#property", labelKey: "Showcase" },
  { href: "/inspiration", labelKey: "Inspiration" },
  { href: "/#pricing", labelKey: "Pricing" },
  { href: "/dashboard", labelKey: "Dashboard" },
] as const;

// Header height for offset calculation (h-16 = 64px) - header is absolute but offset is kept for spacing
const HEADER_HEIGHT = 64;

function setBodyScrollLocked(isLocked: boolean) {
  if (typeof document === "undefined") return;

  document.body.style.overflow = isLocked ? "hidden" : "";
}

/**
 * Smoothly scrolls to a target element by ID, accounting for header offset.
 * Updates URL hash without page jump.
 */
function scrollToSection(hash: string): boolean {
  const id = hash.replace("#", "");
  const element = document.getElementById(id);

  if (!element) return false;

  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
  const offsetPosition = elementPosition - HEADER_HEIGHT - 16; // Extra 16px padding

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });

  // Update URL hash without jumping
  window.history.pushState(null, "", hash);

  return true;
}

/**
 * Compact language selector for the navigation header.
 * Uses setLocaleNoRedirect + a same-document hard reload so the public route
 * tree is rebuilt from the updated locale cookie. The preview/public runtime
 * can keep stale client state across same-route soft refreshes, especially when
 * switching back to the default English locale, so a hard reload is the
 * smallest durable fix for immediate bidirectional switching on `/`.
 */
function NavLanguageSwitcher() {
  const { i18n, t } = useLingui();
  const [isPending, startTransition] = useTransition();

  return (
    <select
      aria-label={t`Select language`}
      className={`rounded border border-white/20 bg-transparent px-1.5 py-0.5 text-xs text-white/70 outline-none transition-colors hover:border-white/40 hover:text-white focus-visible:border-white/50 ${isPending ? "opacity-50 pointer-events-none" : ""}`}
      value={i18n.locale || "en"}
      onChange={(e) => {
        // VAL-I18N-003: stamp cookie synchronously before startTransition so
        // any immediate navigation carries the new locale (React 19 defers
        // async transition callbacks, so the write inside applyPublicLocaleSwitch
        // is not guaranteed to run before a user-initiated navigation).
        const newLocale = e.target.value;
        writeLocaleCookieClient(newLocale);
        startTransition(async () => {
          await applyPublicLocaleSwitch(newLocale);
        });
      }}
    >
      {SUPPORTED_LOCALES.map((locale) => (
        <option key={locale} value={locale} style={{ color: "#111111", background: "#fff" }}>
          {localePickerLabels[locale]}
        </option>
      ))}
    </select>
  );
}

/** Same switcher for the mobile menu (light background context). */
function MobileNavLanguageSwitcher() {
  const { i18n, t } = useLingui();
  const [isPending, startTransition] = useTransition();

  return (
    <select
      aria-label={t`Select language`}
      className={`w-full rounded border border-gray-300 bg-transparent px-2 py-2 text-sm text-[#111111] outline-none transition-colors hover:border-gray-400 focus-visible:border-gray-500 ${isPending ? "opacity-50 pointer-events-none" : ""}`}
      value={i18n.locale || "en"}
      onChange={(e) => {
        // VAL-I18N-003: stamp cookie synchronously before startTransition (same
        // reasoning as NavLanguageSwitcher above).
        const newLocale = e.target.value;
        writeLocaleCookieClient(newLocale);
        startTransition(async () => {
          await applyPublicLocaleSwitch(newLocale);
        });
      }}
    >
      {SUPPORTED_LOCALES.map((locale) => (
        <option key={locale} value={locale}>
          {localePickerLabels[locale]}
        </option>
      ))}
    </select>
  );
}

/**
 * NavigationHeader renders the site header with logo, navigation links,
 * mobile hamburger menu, and CTA buttons that integrate with onboarding.
 *
 * @returns JSX.Element
 */
export function NavigationHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { actions } = useOnboardingState();
  const { t } = useLingui();
  const { data: session, isPending: isSessionPending } = authClient.useSession();
  const [pendingDashboardClick, setPendingDashboardClick] = useState(false);

  // Build localized nav links at render time so translations update with locale
  const navLinks = [
    { href: navLinkHrefs[0].href, label: t`Home` },
    { href: navLinkHrefs[1].href, label: t`Showcase` },
    { href: navLinkHrefs[2].href, label: t`Inspiration` },
    { href: navLinkHrefs[3].href, label: t`Pricing` },
    { href: navLinkHrefs[4].href, label: t`Dashboard` },
  ];

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => {
      const next = !prev;
      setBodyScrollLocked(next);
      return next;
    });
  }, []);

  const closeMobileMenu = useCallback(() => {
    setBodyScrollLocked(false);
    setIsMobileMenuOpen(false);
  }, []);

  const handleLogIn = useCallback(() => {
    actions.openModalWithAuthMode("signin");
  }, [actions]);

  const handleTryFree = useCallback(() => {
    actions.openModalWithAuthMode("signup");
  }, [actions]);

  const openDashboardLogin = useCallback(() => {
    actions.setReturnTo("/dashboard");
    persistAuthRedirect("/dashboard");
    actions.openModalWithAuthMode("signin");
  }, [actions]);

  const handleHardPublicNavigation = useCallback((href: string) => {
    closeMobileMenu();
    navigateToPublicRoute(href);
  }, [closeMobileMenu]);

  // Handle anchor link clicks with smooth scroll on the current page.
  // When the anchor targets a different route (for example /terms -> /#pricing),
  // allow the browser to perform the normal navigation so the homepage can load first.
  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Handle Dashboard link specially: require authentication
    if (href === "/dashboard") {
      closeMobileMenu();
      if (session?.user) {
        // Authenticated — let normal navigation proceed
        return;
      }
      e.preventDefault();
      if (isSessionPending) {
        setPendingDashboardClick(true);
      } else {
        openDashboardLogin();
      }
      return;
    }

    const hashIndex = href.indexOf("#");
    if (hashIndex === -1) {
      e.preventDefault();
      handleHardPublicNavigation(href);
      return;
    }

    const path = href.slice(0, hashIndex) || "/";
    const hash = href.slice(hashIndex);
    if (!hash || hash === "#") return;

    closeMobileMenu();

    if (window.location.pathname !== path) {
      e.preventDefault();
      navigateToPublicRoute(href);
      return;
    }

    e.preventDefault();

    setTimeout(() => {
      scrollToSection(hash);
    }, isMobileMenuOpen ? 150 : 0);
  }, [
    closeMobileMenu,
    handleHardPublicNavigation,
    isMobileMenuOpen,
    isSessionPending,
    openDashboardLogin,
    session?.user,
  ]);

  // Resolve a pending dashboard click once session state is known
  useEffect(() => {
    if (!pendingDashboardClick || isSessionPending) return;
    setPendingDashboardClick(false);
    if (session?.user) {
      window.location.href = "/dashboard";
      return;
    }
    openDashboardLogin();
  }, [pendingDashboardClick, isSessionPending, session?.user, openDashboardLogin]);

  // Handle initial hash on page load
  useMountEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Delay to allow page to fully render
      setTimeout(() => {
        scrollToSection(hash);
      }, 100);
    }
  });

  // Close mobile menu on escape key.
  useMountEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setBodyScrollLocked(false);
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      setBodyScrollLocked(false);
      window.removeEventListener("keydown", handleEscape);
    };
  });

  return (
    <header
      className="absolute top-0 left-0 right-0 z-50 w-full bg-transparent transition-all duration-300 md:top-8 md:left-1/2 md:right-auto md:w-[calc(100%-120px)] md:max-w-[1320px] md:-translate-x-1/2 md:rounded-[12px] md:[background-image:none]"
    >
      <div className="flex h-16 items-center justify-between px-4 md:h-11">
        {/* Logo */}
        <a
          aria-label={t`Home`}
          className="flex items-center gap-2 transition-opacity hover:opacity-90"
          href="/"
          onClick={(e) => {
            if (
              e.defaultPrevented ||
              e.button !== 0 ||
              e.metaKey ||
              e.ctrlKey ||
              e.shiftKey ||
              e.altKey
            ) {
              return;
            }
            e.preventDefault();
            handleHardPublicNavigation("/");
          }}
        >
          <Image
            alt="Aideco logo"
            className="h-8 w-auto"
            height={32}
            priority
            src="/logos/Gemini_Generated_Image_icqk2micqk2micqk-optimized-removebg-preview.png"
            style={{ transform: "scale(2.0)", transformOrigin: "left center" }}
            width={120}
          />
        </a>

        {/* Desktop Navigation */}
        <nav
          aria-expanded={isMobileMenuOpen}
          aria-label={t`Main navigation`}
          className="hidden items-center gap-8 md:flex"
          role="navigation"
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              className="text-sm font-medium text-white/90 transition-colors duration-200 hover:text-white"
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA Buttons (Desktop) */}
        <div className="hidden items-center gap-3 md:flex">
          <NavLanguageSwitcher />
          <Button
            className="!rounded-[6px] !bg-white !text-[#111111] !border-0 hover:!bg-[#f0f0f0]"
            onClick={handleLogIn}
            type="button"
            variant="default"
          >
            <Trans>Log In</Trans>
          </Button>
          <Button
            className="!rounded-[6px] !bg-[#111111] !text-white !border-0 hover:!bg-[#333333]"
            onClick={handleTryFree}
            type="button"
            variant="default"
          >
            <Trans>Try Free</Trans>
          </Button>
        </div>

        {/* Mobile Menu Toggle - z-50 ensures button stays above overlay (z-40) */}
        <button
          aria-expanded={isMobileMenuOpen}
          aria-label={isMobileMenuOpen ? t`Close menu` : t`Open menu`}
          className="relative z-50 flex h-11 w-11 items-center justify-center rounded-md text-white transition-colors hover:bg-white/10 md:hidden"
          onClick={toggleMobileMenu}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleMobileMenu();
            }
          }}
          type="button"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g>
              {/* Top line - transforms to part of X */}
              <path
                d="M3,6 L21,6"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                style={{
                  opacity: isMobileMenuOpen ? 0 : 1,
                  transform: isMobileMenuOpen ? "rotate(45deg)" : "none",
                  transformOrigin: "center",
                  transition: "all 0.3s ease",
                }}
              />
              {/* Middle line - fades out */}
              <path
                d="M3,12 L21,12"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                style={{
                  opacity: isMobileMenuOpen ? 0 : 1,
                  transition: "opacity 0.3s ease",
                }}
              />
              {/* Bottom line - transforms to part of X */}
              <path
                d="M3,12 L21,12"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "rotate(-45deg)" : "none",
                  transformOrigin: "center",
                  transition: "all 0.3s ease",
                }}
              />
              {/* Cross line 1 */}
              <path
                d="M6,6 L18,18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transition: "all 0.3s ease",
                }}
              />
              {/* Cross line 2 */}
              <path
                d="M18,6 L6,18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transition: "all 0.3s ease",
                }}
              />
            </g>
          </svg>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          aria-hidden="true"
          aria-label={t`Close mobile menu`}
          className="fixed inset-0 z-40 bg-black/20 md:hidden"
          onClick={closeMobileMenu}
          role="button"
          tabIndex={-1}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`absolute left-0 right-0 top-16 z-50 border-t border-gray-200 bg-white shadow-lg transition-all duration-300 md:hidden ${
          isMobileMenuOpen
            ? "visible translate-y-0 opacity-100"
            : "invisible -translate-y-4 opacity-0"
        }`}
      >
        <nav
          aria-label={t`Mobile navigation`}
          className="flex flex-col px-4 py-4"
          role="navigation"
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              className="py-3 text-base font-medium text-[#111111] transition-colors duration-200 hover:text-gray-900"
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
            >
              {link.label}
            </a>
          ))}

          {/* Mobile CTA Buttons */}
          <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4">
            <Button
              className="w-full !rounded-[6px] border border-gray-300 bg-transparent text-[#111111] hover:bg-gray-50"
              onClick={() => {
                closeMobileMenu();
                handleLogIn();
              }}
              type="button"
              variant="outline"
            >
              <Trans>Log In</Trans>
            </Button>
            <Button
              className="w-full !rounded-[6px] !bg-[#111111] !text-white hover:!bg-[#333333]"
              onClick={() => {
                closeMobileMenu();
                handleTryFree();
              }}
              type="button"
              variant="default"
            >
              <Trans>Try Free</Trans>
            </Button>
            {/* Mobile language switcher */}
            <MobileNavLanguageSwitcher />
          </div>
        </nav>
      </div>
    </header>
  );
}
